/**
 * Parse dates from various formats used in the API
 * Handles DD.MM.YYYY format from backend and ISO dates
 */
export const parseDate = (dateString: string | undefined | null): string => {
  if (!dateString) return 'Unknown date';
  
  // Check if it's DD.MM.YYYY format
  if (dateString.includes('.')) {
    const parts = dateString.split('.');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      // Return in the same format or convert to locale string
      return `${day}.${month}.${year}`;
    }
  }
  
  // Try to parse as ISO date or other formats
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      }).replace(/\//g, '.');
    }
  } catch {
    // Fall through to return original
  }
  
  // Return original if we can't parse it
  return dateString;
};

/**
 * Parse date for chart display (shorter format)
 */
export const parseDateForChart = (dateString: string | undefined | null): string => {
  if (!dateString) return '';
  
  // Check if it's DD.MM.YYYY format
  if (dateString.includes('.')) {
    const parts = dateString.split('.');
    if (parts.length === 3) {
      const [day, month] = parts;
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthIndex = parseInt(month, 10) - 1;
      return `${day} ${monthNames[monthIndex] || month}`;
    }
  }
  
  // Try to parse as ISO date
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: 'short' 
      });
    }
  } catch {
    // Fall through
  }
  
  return dateString;
};

/**
 * Parse date string to Date object for sorting
 */
export const parseDateToObject = (dateString: string | undefined | null): Date => {
  if (!dateString) return new Date(0); // Return epoch for unknown dates
  
  // Check if it's DD.MM.YYYY format
  if (dateString.includes('.')) {
    const parts = dateString.split('.');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
  }
  
  // Try to parse as ISO date or other formats
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date;
    }
  } catch {
    // Fall through
  }
  
  // Return epoch date if we can't parse
  return new Date(0);
};

/**
 * Format a number with sign (+ or -)
 */
export const formatWithSign = (num: number | undefined | null): string => {
  if (num === undefined || num === null) return '0';
  return num >= 0 ? `+${num}` : `${num}`;
};